import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, 
  ArrowRight, 
  MapPin,
  Check,
  Clock,
  ChevronRight,
  Globe,
  Zap,
  Mail,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CITIES, CITY_ORDER, getLiveCity, getLiveCities, getComingSoonCities } from '../config/cities';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-track-6/artifacts/ylx5ssnx_Insite%20Logo%204.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sliding panel messages for hero section
const heroMessages = [
  {
    badge: "Now Live: NYC + Washington DC",
    headline: "Multi-City Compliance",
    highlight: "Intelligence Platform"
  },
  {
    badge: "Building Compliance Made Simple",
    headline: "Monitor Building",
    highlight: "Compliance Data"
  },
  {
    badge: "For Code Consultants",
    headline: "Streamline Violation",
    highlight: "Research & Reports"
  },
  {
    badge: "For Property Managers",
    headline: "Track Compliance",
    highlight: "Across Your Portfolio"
  },
  {
    badge: "NYC: 41M+ Records | DC: 180K+",
    headline: "Real-Time Access to",
    highlight: "Building Data"
  },
  {
    badge: "For Developers",
    headline: "Pre-Acquisition",
    highlight: "Compliance Insights"
  }
];

export default function CitySelectorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hoveredCity, setHoveredCity] = useState(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [waitlistModal, setWaitlistModal] = useState({ open: false, city: null });
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const liveCity = getLiveCity();
  const liveCities = getLiveCities();
  const comingSoonCities = getComingSoonCities();

  // Auto-rotate hero messages
  useEffect(() => {
    const interval = setInterval(() => {
      setIsSliding(true);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % heroMessages.length);
        setIsSliding(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email) {
      setWaitlistEmail(user.email);
    }
  }, [user]);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistModal.city) return;
    
    setWaitlistLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: waitlistEmail,
          city_id: waitlistModal.city.id,
          city_name: waitlistModal.city.name
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.already_subscribed) {
          toast.info(data.message);
        } else {
          toast.success(data.message);
        }
        setWaitlistModal({ open: false, city: null });
        setWaitlistEmail(user?.email || '');
      } else {
        toast.error(data.detail || 'Failed to join waitlist');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const currentMessage = heroMessages[currentMessageIndex];

  return (
    <div className="min-h-screen bg-[#0a0f1a] relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        {/* Gradient base */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0a0f1a] to-[#0a0f1a]"></div>
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        ></div>
        
        {/* Floating orbs */}
        <div className="absolute top-20 left-[10%] w-72 h-72 bg-blue-500/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute top-40 right-[15%] w-96 h-96 bg-teal-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-[30%] w-80 h-80 bg-cyan-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-40 right-[25%] w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Radial gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0f1a_70%)]"></div>
      </div>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0a0f1a]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Insite360" className="h-8 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-zinc-400 hover:text-white text-sm"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              className="btn-primary rounded-full text-sm px-4 py-2"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Left aligned with logo on right */}
      <section className="relative z-10 pt-20 pb-6 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-6">
            {/* Left - Sliding Text */}
            <div className="flex-1">
              <div 
                className={`inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/30 rounded-full px-3 py-1 mb-3 transition-all duration-300 ${isSliding ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}
              >
                <Zap className="w-3 h-3 text-teal-400" />
                <span className="text-xs text-teal-400">{currentMessage.badge}</span>
              </div>
              
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-2">
                <span className={`block transition-all duration-300 ${isSliding ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0'}`}>
                  {currentMessage.headline}
                </span>
                <span className={`block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 transition-all duration-300 delay-75 ${isSliding ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
                  {currentMessage.highlight}
                </span>
              </h1>

              {/* Slide indicators */}
              <div className="flex items-center gap-2 mb-3">
                {heroMessages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsSliding(true);
                      setTimeout(() => {
                        setCurrentMessageIndex(index);
                        setIsSliding(false);
                      }, 300);
                    }}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      index === currentMessageIndex 
                        ? 'w-6 bg-teal-400' 
                        : 'w-1.5 bg-zinc-600 hover:bg-zinc-500'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              
              <p className="text-sm text-zinc-400 max-w-md">
                Access violations, permits, and compliance records from municipal open data portals.
              </p>
            </div>
            
            {/* Right - Logo */}
            <div className="hidden md:block flex-shrink-0">
              <img src={LOGO_URL} alt="Pivotal Insite360" className="h-56 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Live City - NYC - Compact with Logo */}
      <section className="relative z-10 px-6 pb-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5 bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full text-xs font-medium">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
              Live Now
            </div>
          </div>
          
          {/* Live Cities */}
          <div className="space-y-3">
            {liveCities.map((city) => (
              <div 
                key={city.id}
                className="relative group cursor-pointer"
                onClick={() => navigate(`/${city.id}`)}
              >
                <div 
                  className="absolute inset-0 rounded-xl blur-xl group-hover:blur-2xl transition-all opacity-50 group-hover:opacity-75"
                  style={{ background: `linear-gradient(to right, ${city.color}33, ${city.color}22)` }}
                ></div>
                <div className="relative glass-card border-white/10 rounded-xl p-4 hover:border-white/30 transition-all" style={{ borderColor: `${city.color}33` }}>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${city.color}, ${city.color}aa)` }}
                    >
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold text-white">{city.name}</h2>
                      <p className="text-xs text-zinc-400 truncate">{city.description}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 text-center">
                      <div>
                        <div className="text-sm font-bold text-white">{city.stats.properties}</div>
                        <div className="text-[9px] text-zinc-500">Properties</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-white">{city.stats.violations}</div>
                        <div className="text-[9px] text-zinc-500">Records</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-white">{city.stats.agencies}</div>
                        <div className="text-[9px] text-zinc-500">Sources</div>
                      </div>
                    </div>
                    <Button 
                      className="btn-primary text-xs px-4 py-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${city.id}`);
                      }}
                    >
                      Enter
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Cities - Compact Grid */}
      <section className="relative z-10 px-6 pb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5 bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full text-xs font-medium">
              <Clock className="w-3 h-3" />
              Coming Soon
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {comingSoonCities.map((city) => (
              <div 
                key={city.id}
                className="relative group"
                onMouseEnter={() => setHoveredCity(city.id)}
                onMouseLeave={() => setHoveredCity(null)}
              >
                <div 
                  className="absolute inset-0 rounded-lg blur-lg transition-opacity opacity-0 group-hover:opacity-20"
                  style={{ backgroundColor: city.color }}
                ></div>
                <div className="relative glass-card border-white/10 rounded-lg p-4 hover:border-white/20 transition-all h-full flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <div 
                      className="w-8 h-8 rounded-md flex items-center justify-center"
                      style={{ backgroundColor: `${city.color}20` }}
                    >
                      <MapPin className="w-4 h-4" style={{ color: city.color }} />
                    </div>
                    <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">
                      {city.state}
                    </span>
                  </div>
                  
                  <h3 className="text-sm font-bold text-white mb-1">{city.shortName === city.name ? city.name : city.name}</h3>
                  <p className="text-[10px] text-zinc-500 mb-2">{city.stats.properties} properties</p>
                  
                  <div className="flex flex-wrap gap-1 mb-3 flex-1">
                    {city.dataSources.slice(0, 2).map((source) => (
                      <span 
                        key={source.id}
                        className="text-[9px] bg-white/5 text-zinc-400 px-1.5 py-0.5 rounded"
                      >
                        {source.icon}
                      </span>
                    ))}
                    {city.dataSources.length > 2 && (
                      <span className="text-[9px] text-zinc-600">
                        +{city.dataSources.length - 2}
                      </span>
                    )}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full text-[10px] text-zinc-400 border-zinc-700 hover:border-zinc-600 py-1 h-auto"
                    onClick={() => setWaitlistModal({ open: true, city })}
                    data-testid={`waitlist-btn-${city.id}`}
                  >
                    Join Waitlist
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Multi-City Section - Compact */}
      <section className="relative z-10 px-6 pb-8 border-t border-white/5 pt-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-2">
            One Platform, Every City
          </h2>
          <p className="text-xs text-zinc-400 text-center max-w-lg mx-auto mb-6">
            Whether you manage properties in one city or across the nation, 
            Insite360 gives you a unified view of compliance data.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-1">Portfolio Managers</h3>
              <p className="text-[11px] text-zinc-400">
                Track compliance across properties in multiple cities.
              </p>
            </div>
            
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-br from-teal-500/20 to-teal-500/5 flex items-center justify-center">
                <Globe className="w-5 h-5 text-teal-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-1">National Firms</h3>
              <p className="text-[11px] text-zinc-400">
                Standardize your compliance workflow across all markets.
              </p>
            </div>
            
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-1">Code Consultants</h3>
              <p className="text-[11px] text-zinc-400">
                Expand your services with instant access to local data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Compact */}
      <footer className="relative z-10 border-t border-white/5 py-4 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Insite360" className="h-6 w-auto" />
            <span className="text-zinc-500 text-xs">© 2026 Pivotal Insite360</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            <a href="mailto:support@insite360.com" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal */}
      {waitlistModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setWaitlistModal({ open: false, city: null })}
          />
          
          {/* Modal */}
          <div className="relative bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-md p-6 shadow-2xl">
            {/* Close button */}
            <button
              onClick={() => setWaitlistModal({ open: false, city: null })}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                <MapPin className="w-7 h-7 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">
                Join the {waitlistModal.city?.name} Waitlist
              </h3>
              <p className="text-sm text-zinc-400">
                Be the first to know when we launch in {waitlistModal.city?.shortName || waitlistModal.city?.name}
              </p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleJoinWaitlist} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    className="input-dark pl-10 h-11"
                    required
                    data-testid="waitlist-email-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={waitlistLoading || !waitlistEmail}
                className="w-full h-11 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium"
                data-testid="waitlist-submit-btn"
              >
                {waitlistLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Join Waitlist
                  </>
                )}
              </Button>
            </form>
            
            {/* Info */}
            <p className="text-[10px] text-zinc-600 text-center mt-4">
              We'll only email you when {waitlistModal.city?.shortName || waitlistModal.city?.name} launches. No spam.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
